import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { IconClock, IconInfo } from '../../utils/icons';

export type NoticeTone = 'info' | 'wait' | 'blocked';

export interface ConfirmStepNoticeProps {
    tone: NoticeTone;
    title: string;
    body: string;
    names?: string[];
}

const TONES = {
    info: { bg: '#F7F7F5', border: '#ECECEC', accent: '#8A8A8A', title: '#4A4A4A' },
    wait: { bg: '#FBF8F3', border: '#EBDCC4', accent: '#AB854A', title: '#8A6A2F' },
    blocked: { bg: '#FDF6F5', border: '#F0DCD9', accent: '#B4544A', title: '#9E453B' },
};

const PULSE = {
    '@keyframes confirmStepPulse': {
        '0%, 100%': { opacity: 0.35, transform: 'scale(0.85)' },
        '50%': { opacity: 1, transform: 'scale(1)' },
    },
    animation: 'confirmStepPulse 1.8s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
};

export const ConfirmStepNotice: FC<ConfirmStepNoticeProps> = ({ tone, title, body, names }) => {
    const palette = TONES[tone];
    const Glyph = tone === 'wait' ? IconClock : IconInfo;

    return (
        <Box
            sx={{
                mx: 2,
                mb: 1.5,
                p: 1.75,
                display: 'flex',
                gap: 1.25,
                borderRadius: '14px',
                backgroundColor: palette.bg,
                border: `1px solid ${palette.border}`,
            }}
        >
            <Box sx={{ position: 'relative', flexShrink: 0, pt: '1px' }}>
                <Glyph size={18} stroke={palette.accent} />
                {tone === 'wait' && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -2,
                            right: -3,
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            backgroundColor: palette.accent,
                            ...PULSE,
                        }}
                    />
                )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: palette.title, lineHeight: 1.35 }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#7A7A7A', lineHeight: 1.5, mt: 0.4 }}>
                    {body}
                </Typography>
                {!!names?.length && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 1 }}>
                        {names.map((name) => (
                            <Box
                                key={name}
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: palette.title,
                                    backgroundColor: '#fff',
                                    border: `1px solid ${palette.border}`,
                                    borderRadius: '8px',
                                    px: 0.8,
                                    py: 0.2,
                                }}
                            >
                                {name}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ConfirmStepNotice;
